import { useState, useEffect } from "react";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import ReceiptPhotoIntro from "./ReceiptPhotoIntro";
import MultiPhotoCameraCapture from "./MultiPhotoCameraCapture";
import PhotoReviewScreen from "./PhotoReviewScreen";
import ReceiptDataReviewScreen from "./ReceiptDataReviewScreen";

interface ReceiptScannerFlowProps {
  open: boolean;
  onClose: () => void;
  onComplete: (imageUrls: string[], data: any) => void;
  extractedData?: any;
  capturedImages?: string[];
}

type FlowStep = 'intro' | 'camera' | 'photo-review' | 'data-review';

export default function ReceiptScannerFlow({ 
  open, 
  onClose, 
  onComplete, 
  extractedData,
  capturedImages: externalCapturedImages
}: ReceiptScannerFlowProps) {
  const [currentStep, setCurrentStep] = useState<FlowStep>('intro');
  const [capturedImages, setCapturedImages] = useState<string[]>(externalCapturedImages || []);
  const [receiptData, setReceiptData] = useState<any>(extractedData || null);

  // Update internal state when external data changes
  useEffect(() => {
    if (extractedData && Object.keys(extractedData).length > 0) {
      setReceiptData(extractedData);
      setCurrentStep('data-review');
    }
  }, [extractedData]);

  useEffect(() => {
    if (externalCapturedImages && externalCapturedImages.length > 0) {
      setCapturedImages(externalCapturedImages);
      if (extractedData && Object.keys(extractedData).length > 0) {
        setCurrentStep('data-review');
      }
    }
  }, [externalCapturedImages, extractedData]);

  const handleStartCamera = () => {
    setCurrentStep('camera');
  };

  const handleCaptureComplete = (imageUrls: string[]) => {
    setCapturedImages(imageUrls);
    setCurrentStep('photo-review');
  };

  const handleDelete = (index: number) => {
    setCapturedImages(prev => {
      const newImages = [...prev];
      URL.revokeObjectURL(newImages[index]);
      newImages.splice(index, 1);
      return newImages;
    });
  };

  const handleAddMore = () => {
    setCurrentStep('camera');
  };

  const handlePhotoReviewNext = () => {
    // Move to data review (will be triggered by parent after AI parsing)
    onComplete(capturedImages, null);
  };

  const handleDataReviewConfirm = (editedData: any) => {
    onComplete(capturedImages, editedData);
    handleClose();
  };

  const handleDataReviewBack = () => {
    setCurrentStep('photo-review');
  };

  const handleBack = () => {
    // Clean up captured images
    capturedImages.forEach(url => URL.revokeObjectURL(url));
    setCapturedImages([]);
    setCurrentStep('intro');
  };

  const handleClose = () => {
    // Clean up
    capturedImages.forEach(url => URL.revokeObjectURL(url));
    setCapturedImages([]);
    setCurrentStep('intro');
    setReceiptData(null);
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-full h-screen p-0 gap-0 [&>button]:hidden">
        {currentStep === 'intro' && (
          <ReceiptPhotoIntro onStartCamera={handleStartCamera} />
        )}
        {currentStep === 'camera' && (
          <MultiPhotoCameraCapture 
            onCapture={handleCaptureComplete}
            onBack={handleBack}
          />
        )}
        {currentStep === 'photo-review' && (
          <PhotoReviewScreen
            imageUrls={capturedImages}
            onDelete={handleDelete}
            onAddMore={handleAddMore}
            onNext={handlePhotoReviewNext}
          />
        )}
        {currentStep === 'data-review' && receiptData && (
          <ReceiptDataReviewScreen
            data={receiptData}
            imageUrls={capturedImages}
            onConfirm={handleDataReviewConfirm}
            onBack={handleDataReviewBack}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}
